/**
 * Admin 认证状态管理（Zustand）
 *
 * 管理 Admin JWT、用户名、角色。
 * 初始状态从 localStorage 同步读取，避免首屏闪烁和错误跳转。
 */

import { create } from 'zustand'
import { STORAGE_KEYS } from '@chaoyinli/shared'
import type { AdminLoginResponse } from '@/api/services'

interface AdminAuthState {
  // ===== State =====
  jwt: string | null
  username: string | null
  role: string | null
  isAuthenticated: boolean

  // ===== Actions =====
  /** 保存认证信息到 state + localStorage */
  setAuth: (data: AdminLoginResponse) => void
  /** 从 localStorage 恢复状态 */
  loadFromStorage: () => void
  /** 清除 state + localStorage */
  logout: () => void
}

/** localStorage 键名 */
const LS_USERNAME = 'cy_admin_username'
const LS_ROLE = 'cy_admin_role'

/**
 * 从 localStorage 同步读取初始状态
 */
function getInitialState() {
  const jwt = localStorage.getItem(STORAGE_KEYS.ADMIN_JWT)
  const username = localStorage.getItem(LS_USERNAME)
  const role = localStorage.getItem(LS_ROLE)

  return {
    jwt: jwt ? jwt.replace(/^"|"$/g, '') : null,
    username: username ? username.replace(/^"|"$/g, '') : null,
    role: role ? role.replace(/^"|"$/g, '') : null,
    isAuthenticated: !!jwt,
  }
}

export const useAdminStore = create<AdminAuthState>((set) => ({
  // ===== 初始 State（从 localStorage 同步读取）=====
  ...getInitialState(),

  // ===== Actions =====
  setAuth: (data: AdminLoginResponse) => {
    localStorage.setItem(STORAGE_KEYS.ADMIN_JWT, data.jwt)
    localStorage.setItem(LS_USERNAME, data.username)
    localStorage.setItem(LS_ROLE, data.role)
    set({
      jwt: data.jwt,
      username: data.username,
      role: data.role,
      isAuthenticated: true,
    })
  },

  loadFromStorage: () => {
    const jwt = localStorage.getItem(STORAGE_KEYS.ADMIN_JWT)
    const username = localStorage.getItem(LS_USERNAME)
    const role = localStorage.getItem(LS_ROLE)

    if (jwt) {
      set({
        jwt: jwt.replace(/^"|"$/g, ''),
        username: username ? username.replace(/^"|"$/g, '') : null,
        role: role ? role.replace(/^"|"$/g, '') : null,
        isAuthenticated: true,
      })
    } else {
      set({
        jwt: null,
        username: null,
        role: null,
        isAuthenticated: false,
      })
    }
  },

  logout: () => {
    localStorage.removeItem(STORAGE_KEYS.ADMIN_JWT)
    localStorage.removeItem(LS_USERNAME)
    localStorage.removeItem(LS_ROLE)
    set({
      jwt: null,
      username: null,
      role: null,
      isAuthenticated: false,
    })
  },
}))
